import React, {useMemo, useState} from 'react';
import axios from 'axios';
import {useMutation} from '@tanstack/react-query';
import Modal from '../../components/Modal';
import ErrorAlert from '../../components/error_alert';

const REMINDER_TYPES = [
    {value: '-90_pre_expiration', label: '90 days before expiry'},
    {value: '-60_pre_expiration', label: '60 days before expiry'},
    {value: '-30_pre_expiration', label: '30 days before expiry'},
    {value: '-14_pre_expiration', label: '14 days before expiry'},
    {value: '-7_pre_expiration', label: '7 days before expiry'},
    {value: '-1_pre_expiration', label: '1 day before expiry'},
    {value: '1_expiration', label: '1 day after expiry'},
    {value: '7_expiration', label: '7 days after expiry'},
    {value: '14_expiration', label: '14 days after expiry'},
    {value: '30_pre_deletion', label: '30 days after expiry (deletion warning)'},
    {value: '59_pre_deletion', label: '59 days after expiry (final warning)'},
    {value: '60_deletion', label: '60 days after expiry (deletion notice)'},
];

function postJson(path, body) {
    return axios({
        url: `${process.env.REACT_APP_API_URL}${path}`,
        method: 'POST',
        data: body,
        withCredentials: true,
    }).then((r) => r?.data);
}

export default function ManualReminderDialog({subscription, onClose, onSent}) {
    const [type, setType] = useState('-90_pre_expiration');
    const [preview, setPreview] = useState(null); // {subject, html, recipients, cc, alreadySent}
    const [confirmSend, setConfirmSend] = useState(false);

    const previewMutation = useMutation({
        mutationFn: () => postJson('/subscription-notification-preview', {
            subscriptionId: subscription.id,
            notificationType: type,
        }),
        onSuccess: (res) => {
            if (res?.ok) setPreview(res.data);
        },
    });

    const sendMutation = useMutation({
        mutationFn: () => postJson('/subscription-notification-manual-send', {
            subscriptionId: subscription.id,
            notificationType: type,
        }),
        onSuccess: (res) => {
            if (res?.ok) {
                onSent?.(res.data);
                onClose?.();
            }
        },
    });

    const previewError = previewMutation.isError ? previewMutation.error : null;
    const sendError = sendMutation.isError ? sendMutation.error : null;

    const handleTypeChange = (e) => {
        setType(e.target.value);
        setPreview(null);
        setConfirmSend(false);
    };

    const iframeSrcDoc = useMemo(() => preview?.html || '', [preview]);

    return (
        <Modal
            isOpen={!!subscription}
            onClose={onClose}
            title="Send Subscription Reminder"
            subtitle={subscription?.client?.name
                ? `${subscription.client.name} — expires ${subscription.end ? new Date(subscription.end).toLocaleDateString() : '—'}`
                : ''}
            size="tw-max-w-5xl"
        >
            <div className="tw-flex tw-flex-col tw-gap-4 tw-max-h-[75vh]">
                <div className="tw-flex tw-items-end tw-gap-3">
                    <label className="tw-flex-1 tw-flex tw-flex-col tw-gap-1">
                        <span className="tw-text-sm tw-font-medium">Reminder type</span>
                        <select
                            className="tw-select tw-select-bordered tw-w-full"
                            value={type}
                            onChange={handleTypeChange}
                            disabled={previewMutation.isPending || sendMutation.isPending}
                        >
                            {REMINDER_TYPES.map((t) => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </label>
                    <button
                        className="tw-btn tw-btn-outline"
                        onClick={() => previewMutation.mutate()}
                        disabled={previewMutation.isPending || sendMutation.isPending}
                    >
                        {previewMutation.isPending ? 'Loading…' : 'Preview email'}
                    </button>
                </div>

                <ErrorAlert error={previewError || sendError} />

                {preview && (
                    <div className="tw-flex tw-flex-col tw-gap-2 tw-flex-1 tw-min-h-0">
                        <div className="tw-text-sm tw-space-y-1">
                            <div><span className="tw-font-medium">Subject:</span> {preview.subject}</div>
                            <div><span className="tw-font-medium">Recipients ({preview.recipients?.length || 0}):</span>{' '}
                                {(preview.recipients || []).map((r) => r.email).join(', ') || <em className="tw-text-gray-400">no recipients</em>}
                            </div>
                            {preview.cc?.length > 0 && (
                                <div><span className="tw-font-medium">Cc:</span> {preview.cc.join(', ')}</div>
                            )}
                            {preview.alreadySent && (
                                <div className="tw-rounded tw-bg-yellow-100 tw-border tw-border-yellow-400 tw-text-yellow-800 tw-px-3 tw-py-1">
                                    This reminder type was previously marked as sent for this subscription.
                                </div>
                            )}
                        </div>
                        <iframe
                            title="Email preview"
                            srcDoc={iframeSrcDoc}
                            className="tw-w-full tw-flex-1 tw-min-h-[300px] tw-border tw-rounded"
                            sandbox=""
                        />
                    </div>
                )}

                <div className="tw-flex tw-justify-between tw-items-center tw-pt-3 tw-border-t">
                    <div className="tw-text-xs tw-text-gray-500">
                        {preview?.recipients?.length
                            ? `Will send to ${preview.recipients.length} user(s).`
                            : 'Preview first to see recipients.'}
                    </div>
                    <div className="tw-flex tw-gap-2">
                        <button className="tw-btn" onClick={onClose} disabled={sendMutation.isPending}>Cancel</button>
                        {!confirmSend ? (
                            <button
                                className="tw-btn tw-btn-primary"
                                onClick={() => setConfirmSend(true)}
                                disabled={!preview || !preview.recipients?.length || sendMutation.isPending}
                            >
                                Send…
                            </button>
                        ) : (
                            <>
                                <button
                                    className="tw-btn tw-btn-outline"
                                    onClick={() => setConfirmSend(false)}
                                    disabled={sendMutation.isPending}
                                >
                                    Back
                                </button>
                                <button
                                    className="tw-btn tw-btn-error"
                                    onClick={() => sendMutation.mutate()}
                                    disabled={sendMutation.isPending}
                                >
                                    {sendMutation.isPending ? 'Sending…' : 'Confirm send'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
}
